<template>
  <!-- sidebar -->
  <div ref="aside">
    <div
      class="sidebar-overlay"
      :class="{ 'sidebar-overlay--visible': sidebarOpen }"
      @click="toggleOverlay()"
    ></div>
    <div
      class="sidebar"
      :class='{"sidebar--open" : sidebarOpen}'
    >
      <nav class="sidebar__nav" ref="nav">
        <ul class="sidebar__nav-list">
          <li v-for="navItem in navItems" :key="navItem.label" class="sidebar__nav-list-item">
            <SidebarComponent
              :item="navItem"
              :open="submenus[`${navItem.key}Open`]"
              :callback="toggleSubmenu"
              :page="item"
            />
            <ul
              v-if="'subitems' in navItem"
              class="sidebar__nav-subitems"
              :class="{ open: submenus[`${navItem.key}Open`] }"
            >
              <li
                v-for="subitem in navItem.subitems" :key="subitem.label"
                class="sidebar__nav-list-subitem"
              >
                <SidebarComponent
                  :item="subitem"
                  :page="item"
                  subitem
                />
              </li>
            </ul>
          </li>
        </ul>
      </nav>
      <div class="sidebar__button" v-if="showLimitsbutton">
        <a href="javascript:;" @click="openLimitsLink" class="button-unbox button-unbox--primary button-unbox--compact">
          <span class="button-unbox__content">
            <span class="button-unbox__icon">
              <ion-icon class="ion-icon" name="add-circle-outline" />
            </span>
            <span class="button-unbox__text">Limite de envios</span>
          </span>
        </a>
      </div>
      <div class="sidebar__nav sidebar__subitems">
        <ul class="sidebar__nav-list">
          <li v-for="subitem in navSubItems" :key="item.label" class="sidebar__nav-list-item">
            <SidebarComponent :item="subitem" />
          </li>
        </ul>
      </div>
      <div class="sidebar__close">
        <button
          class="sidebar__close__button"
          @click="toggleSidebar"
        >
          <ion-icon class="icon" name="close-sharp" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import checkFeatureToggle from 'commons/js/mixins/verifyFeatureTag';
import CheckFeatureTester from 'commons/js/mixins/checkFeatureTester';
import { HTTP_OK } from 'commons/js/HttpStatusCodes';
import { INTERNAL_LINKS } from 'commons/js/enums/links';

import SidebarComponent from './SidebarComponent.vue';

const featuresToCheck = [
  {
    name: 'hasClassicMySales',
    value: 'pti-mv-classic',
  }
];

export default {
  props: {
    item: {
      type: String,
      required: true
    },
    limitAction: {
      type: String,
      required: false
    },
  },

  components: {
    SidebarComponent,
  },

  data() {
    return {
      showMask: false,
      showMenu: false,
      menuMobile: false,
      meusLinksUrl: "",
      documentsDashUrl: "",
      configurationsDashUrl: "",
      recurrentDestinationsDashUrl: "",
      integrationRoutes: {
        catalog: MEConfig.get('dash.url') + '/integracoes',
        tables: '/painel/integracoes/gerador-de-tabelas',
        permissions: MEConfig.get('dash.url') + '/integracoes/permissoes-de-acesso',
        areaDev: MEConfig.get('dash.url') + '/integracoes/area-dev'
      },
      pendingPurchases: 0,
      pendingConciliations: 0,
      submenus: {
        integrationsOpen: false,
        configurationsOpen: false,
        shipmentsOpen: false
      },
    };
  },

  mixins: [checkFeatureToggle, CheckFeatureTester(featuresToCheck)],

  computed: {
    ...mapState('meusEnvios', ['releasedOrders', 'cancellingOrders']),
    ...mapGetters('general', ['sidebarOpen']),
    isMySalesFeatureUser() {
      return MEFeatureFlag.enabled('pti-my-sales-v2');
    },
    mySalesRedirectUrl() {
      return !this.hasClassicMySales
        ? MEConfig.get('dash.url') + '/minhas-vendas'
        : '/painel/minhas-vendas';
    },

    showLimitsbutton() {
      return parseInt(this.limitAction) === 1 || parseInt(this.limitAction) === 2;
    },

    limitsUrl() {
      if (parseInt(this.limitAction) === 1) {
        return '/painel/gerenciar/documentos';
      }

      return 'https://menv.io/posso-ter-um-limite-de-etiquetas-simultaneas-maior-que-50';
    },

    limitsTarget() {
      if (parseInt(this.limitAction) === 1) {
        return '_self';
      }

      return '_blank';
    },

    navItems() {
      return [
        {
          key: 'home',
          label: 'Página inicial',
          icon: 'home-outline',
          href: INTERNAL_LINKS.HOME,
          badge: {},
          visible: () => true,
        },
        {
          key: 'calculadora',
          label: 'Calculadora',
          icon: 'calculator-outline',
          href: INTERNAL_LINKS.CALCULATOR,
          badge: {},
          visible: () => true,
        },
        {
          label: 'Meus envios',
          icon: 'cube-outline',
          badge: {
            content: this.getCounterLabel(this.pendingPurchases),
            visible: this.pendingPurchases,
          },
          key: 'shipments',
          visible: () => true,
          subitems: [
            {
              key: 'liberados',
              label: 'Liberados e postados',
              href: '/painel/meus-envios#liberados',
              badge: {},
            },
            {
              key: 'aguardando',
              label: 'Aguardando pagamento',
              href: '/painel/meus-envios/aguardando-pagamento',
              badge: {},
            },
          ],
        },
        {
          key: 'carteira',
          label: 'Melhor carteira',
          icon: 'wallet-outline',
          href: INTERNAL_LINKS.WALLET.HOME,
          badge: {},
          visible: () => true,
        },
        {
          key: 'links-frete',
          label: 'Meus links de frete',
          icon: 'link',
          href: this.meusLinksUrl,
          badge: {},
          visible: () => true,
        },
        {
          key: 'conferidas',
          label: 'Postagens conferidas',
          icon: 'receipt-outline',
          href: INTERNAL_LINKS.DEBTS,
          badge: {
            content: this.getCounterLabel(this.pendingConciliations),
            visible: this.pendingConciliations,
          },
          visible: () => true,
        },
        {
          key: 'minhas-vendas',
          label: 'Minhas vendas',
          icon: 'bag-handle-outline',
          href: this.mySalesRedirectUrl,
          badge: {},
          visible: () => true,
        },
        {
          label: 'Integrações',
          icon: 'extension-puzzle-outline',
          key: 'integrations',
          visible: () => true,
          subitems: [
            {
              key: 'catalogo',
              label: 'Catálogo',
              href: this.integrationRoutes.catalog,
              badge: {},
            },
            {
              key: 'tabelas',
              label: 'Tabelas',
              href: this.integrationRoutes.tables,
              badge: {},
            },
            {
              key: 'permissoes',
              label: 'Permissões de Acesso',
              href: this.integrationRoutes.permissions,
              badge: {},
            },
            {
              key: 'area-dev',
              label: 'Área Dev.',
              href: this.integrationRoutes.areaDev,
              badge: {},
            },
          ],
        },
        {
          label: 'Gerenciar',
          icon: 'settings-outline',
          key: 'configurations',
          visible: () => true,
          subitems: [
            {
              key: 'perfil',
              label: 'Perfil',
              href: '/painel/gerenciar/perfil',
              badge: {},
            },
            {
              key: 'lojas',
              label: 'Lojas',
              href: '/painel/gerenciar/lojas',
              badge: {},
            },
            ...(MEFeatureFlag.enabled('xpc-destinatarios-recorrentes') ? [
              {
                key: 'destinatarios-recorrentes',
                label: 'Destinatários recorrentes',
                href: this.recurrentDestinationsDashUrl,
                badge: {}
              }
            ] : []),
            {
              key: 'verificacao',
              label: 'Verificação de conta',
              href: this.documentsDashUrl,
              badge: {},
            },
            {
              key: 'cupons',
              label: 'Cupons',
              href: '/painel/gerenciar/cupons',
              badge: {},
            },
            {
              key: 'configuracoes',
              label: 'Configurações',
              href: this.configurationsDashUrl,
              badge: {},
            },
          ],
        },
        {
          key: 'afiliados',
          label: 'Afiliados',
          icon: 'people-outline',
          href: '/painel/afiliados',
          badge: {},
          visible: () => true,
        },
        {
          key: 'notificacoes',
          label: 'Notificações',
          icon: 'notifications-outline',
          href: INTERNAL_LINKS.NOTIFICATIONS,
          badge: {},
          visible: () => true,
        },
      ];
    },

    navSubItems() {
      return [
        {
          label: 'Ajuda',
          icon: 'help-circle-outline',
          ion: true,
          href: INTERNAL_LINKS.HELP,
          badge: {},
        },
      ];
    }
  },

  async created() {
    this.getPendingPurchases();
    this.getPendingConciliations();
  },

  mounted() {
    this.$root.$asideMenu = this.$refs.aside;
    this.meusLinksUrl = MEConfig.get('dash.url') + '/meus-links-de-frete';
    this.documentsDashUrl = MEConfig.get('dash.url') + '/documentos';
    this.configurationsDashUrl = MEConfig.get('dash.url') + '/configuracoes';
    this.recurrentDestinationsDashUrl = MEConfig.get('dash.url') + '/destinatarios-recorrentes';

    if (['catalogo', 'tabelas', 'permissoes', 'area-dev'].includes(this.item)) {
      this.submenus.integrationsOpen = true;
    }

    if (['perfil', 'lojas', 'verificacao', 'cupons', 'configuracoes'].includes(this.item)) {
      this.submenus.configurationsOpen = true;
    }

    if (['liberados', 'aguardando'].includes(this.item)) {
      this.submenus.shipmentsOpen = true;
    }
  },

  methods: {
    ...mapActions('meusEnvios', ['setPendingPurchases']),

    ...mapActions('general', ['changeSidebarStatus']),

    isEqualOrHigherThanOneHundred(counter) {
      return counter >= 100;
    },

    getCounterLabel(data) {
      return this.isEqualOrHigherThanOneHundred(data) ? '99+' : data;
    },

    async getPendingPurchases() {
      try {
        const {data: {count}, status} = await axios.get('/api/v2/me/purchases/pending/count');

        if (status === HTTP_OK) {
          this.pendingPurchases = count;
          this.setPendingPurchases(count);
        }
      } catch(error) {
        this.pendingPurchases = 0;
        this.setPendingPurchases();
      }
    },

    async getPendingConciliations() {
      try {
        const {data: {count}, status} = await axios.get('/api/v2/me/conciliations/distinct-metrics/count?increment=false');

        if (status === HTTP_OK) {
          this.pendingConciliations = count;
        }
      } catch(error) {
        this.pendingConciliations = 0;
      }
    },

    showModal(modal) {
      this[modal] = true;
      this.showMask = true;

      let self = this;
      window.addEventListener("keyup", function keyPress(e) {
        let key = e.which || e.keyCode;

        if (key === 27) self.hideAllModals();

        window.removeEventListener("keyup", keyPress);
      });
    },

    hideModal(modal) {
      this[modal] = false;
      this.showMask = false;
    },

    hideAllModals() {
      this.showMask = false;
      this.modalCalculator = false;
    },

    async toggleSidebar() {
      this.changeSidebarStatus(!this.sidebarOpen);

      await this.$nextTick();

      // close submenu when sidebar is closed
      if (this.sidebarOpen === false) {
        window.setTimeout(() => {
          this.$refs.nav.scrollTo({
            top: 0,
            behavior: 'instant',
          });
        }, 300);

        this.closeSubmenus();
      }
    },

    toggleSubmenu(menu, evt) {
      const submenu = `${menu}Open`;
      const element = evt.currentTarget;
      const isRootElement = element.classList.contains('sidebar__nav-list-item');
      const rootElement = isRootElement ? evt.currentTarget : evt.currentTarget.parentNode.parentNode;

      if (submenu in this.submenus) {
        this.submenus[submenu] = !this.submenus[submenu];

        this.closeSubmenus(submenu);
      }

      // open sidebar when submenu is opened
      if (window.matchMedia("(max-width: 1279px)").matches) {
        if (this.submenus[`${menu}Open`] === true) {
          this.changeSidebarStatus(true);
        }
      }

      if (this.submenus[submenu]) {
        window.setTimeout(() => {
          const { offsetTop, offsetHeight } = rootElement;
          const scrollBottom = (offsetTop + offsetHeight) - this.$refs.nav.offsetHeight;

          this.$refs.nav.scrollTo({
            top: scrollBottom,
            behavior: 'smooth',
          });
        }, 50);
      }
    },

    // close sidebar and submenu when overlay is clicked
    toggleOverlay() {
      this.closeSubmenus();
      this.toggleSidebar();
    },

    // Close all submenus if any submenus is provided.
    closeSubmenus(clickedSubmenu = null) {
      const submenus = Object.keys(this.submenus);

      if (clickedSubmenu) {
        // Close all the submenus that aren't the clicked menu
        submenus
          .filter(key => key !== clickedSubmenu)
          .forEach(submenu => {
            this.submenus[submenu] = false;
          });
      } else {
        submenus.forEach(submenu => {
          this.submenus[submenu] = false;
        });
      }
    },

    openLimitsLink() {
      try {
        gtag('event', 'click', {
          'event_category': 'navbar-limit-action',
          'event_label': 'mais limite de envios'
        });
      } catch (error) {
        console.log('gtag error:', error);
      }
      window.open(this.limitsUrl, this.limitsTarget);
    },
  }
};
</script>
